import Api from "@/main/api";
import store from "@/main/store";
import { prepGet, loginCredentialToJson } from "@/shared/webauthn";

/**
 * Step-up verification seam.
 *
 * This is the SINGLE place that turns "verify the current user" into backend
 * calls. When real backend enforcement arrives (step-up token / session window),
 * change only this file — StepUpDialog and every guarded x-button stay untouched.
 *
 * (C) 2026
 */

function currentUserId() {
  const user = store.getters.getUser;
  // user may be { user: { userId } } or { userId } (see LockScreen.vue).
  return (user && (user.user ? user.user.userId : user.userId)) || null;
}

const StepUpService = {
  /**
   * Verify the current user's TOTP code in-session.
   * @param {string} code 6-digit authenticator code
   * @param {string} [userId] explicit userId (callers in non-admin portals must
   *   pass this — the module-level store is the admin store and is empty there).
   * @returns {Promise<boolean>} true on success, false on wrong code
   */
  verifyTotp: async (code, userId = null) => {
    const res = await Api.auth.verifyMfa({ userId: userId || currentUserId(), code });
    return !!res.data;
  },

  /**
   * Verify the current user's passkey in-session (no new login session).
   * Obtains a single-use verificationToken from the backend; the token is NOT
   * sent to gated endpoints yet (enforcement deferred), so this is a frontend
   * possession-proof for now.
   * @returns {Promise<boolean>} true on a successful assertion
   */
  verifyPasskey: async () => {
    const { data: opts } = await Api.passkey.verifyStart();
    const publicKey = prepGet(JSON.parse(opts.publicKeyOptions).publicKey);
    const cred = await navigator.credentials.get({ publicKey });
    const { data } = await Api.passkey.verifyFinish({
      challengeHandle: opts.challengeHandle,
      credential: loginCredentialToJson(cred),
    });
    return !!(data && data.verificationToken);
  },
};

export default StepUpService;
